import clsx from 'clsx';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useParams, useHistory } from 'react-router';
import { withStyles } from '@material-ui/core';
import React, { createElement, useContext, useEffect, useState } from 'react';

import BreadCrumbs from '@componentsV2/BreadCrumbs';
import Box from '../../../../components/atomic/Box';
import useDesktop from '../../../../hooks/useDesktop';
import SearchField from '../../../../components/SearchField';
import useFetchEffect from '../../../../hooks/useFetchEffect';
import EmptyMessage from '../../../../components/EmptyMessage';
import ErrorContainer from '../../../../components/ErrorContainer';
import { PUBLICSECTIONFORBIDDENERROR } from '../../../../constants';
import {
  acSetDocuments,
  acSetDocumentSearchText,
  acSetFetchingDocuments,
  acSetFetchingDocumentsError,
  acSetPropertyDocumentFolders,
  acSetPropertyDocumentFoldersToRender,
  acSetPropertyFoldersBreadCrumbs,
  acSetSelectedPropertyFolderId,
} from '../../../../store/actions/documents';
import {
  sGetDocuments,
  sGetDocumentSearchText,
  sGetFetchingDocuments,
  sGetFetchingDocumentsError,
  sGetPropertyDocumentFolders,
  sGetPropertyDocumentFoldersToRender,
  sGetPropertyFoldersBreadCrumbs,
} from '../../../../store/reducers/documents';
import styles from '../../../PrivateRoutes/Dashboard/Documents/style';
import { getPublicDocuments as apiFetchDocuments } from '../../../../api/documents';
import {
  getDocumentList,
  getDocumentTable,
} from '../../../../utils/DocumentTable';
import WithHeader from '../../../../components/layouts/WithHeader';
import MobileDocumentSearch from '../../../PrivateRoutes/Dashboard/Documents/MobileDocumentSearch';
import { FileUploadContext } from '../../../../contexts/fileUpload';
import { fetchPublicPropertyFolders } from '../../../../api/properties';
import Typography from '../../../../components/atomic/Typography';
import useDebounce from '../../../../hooks/useDebounce';

const Documents = ({
  classes,
  documents,
  setDocuments,
  searchText,
  setSearchText,
  propertyDocumentFolders,
  propertyDocumentFoldersToRender,
  propertyFoldersBreadCrumbs,
  setPropertyDocumentFolders,
  setPropertyDocumentFoldersToRender,
  setPropertyFoldersBreadCrumbs,
  setSelectedPropertyFolderId,
  setFetching,
  setFetchingError,
  fetching,
  fetchingError,
}) => {
  const history = useHistory();
  const isDesktop = useDesktop();
  const { boligmappaNumber } = useParams();
  const [headerHeight, setHeaderHeight] = useState(0);
  const [stickyFiltersHeight, setStickyFiltersHeight] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const documentDebouncedSearch = useDebounce(searchText, 500);
  const [searchQuery, setSearchQuery] = useState(searchText);
  const [currentDocFolderId, setCurrentDocFolderId] = useState(null);
  const [propertyDocumentsToRender, setPropertyDocumentsToRender] = useState(
    []
  );
  const { directoryId, setDirectoryId, directoryTree, setDirectoryTree } =
    useContext(FileUploadContext);

  const onSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const launchDocsAndFoldersState = ({ folders, docs }) => {
    if (folders) {
      setPropertyDocumentFolders(folders);
      setPropertyDocumentFoldersToRender(folders);
      setSelectedPropertyFolderId(null);
    }
    if (docs) {
      const docsToRender = docs.filter((el) => el.directoryId === null);
      setDocuments(docs);
      setPropertyDocumentsToRender(docsToRender);
    }
  };

  useFetchEffect({
    dependencies: [boligmappaNumber],
    conditionFunction: () => propertyDocumentFolders?.length === 0,
    onSuccess: (data) => {
      launchDocsAndFoldersState({ folders: data.directories.reverse() });
    },
    onError: (error) => setFetchingError(error),
    apiFetchFunction: () => fetchPublicPropertyFolders(boligmappaNumber),
  });

  useFetchEffect({
    dependencies: [boligmappaNumber],
    onSuccess: (data) => {
      launchDocsAndFoldersState({ docs: data });
    },
    setFetchingFunction: (value) => setFetching(value),
    onError: (error) => setFetchingError(error),
    apiFetchFunction: () =>
      apiFetchDocuments(boligmappaNumber, { includeTaggedInfo: true }),
  });

  useEffect(() => {
    if (documents) {
      setPropertyDocumentsToRender(
        documents.filter((doc) => doc.directoryId === currentDocFolderId)
      );
    }
  }, [documents]);

  const showEmptyListMessage =
    !fetching && Array.isArray(documents) && documents.length === 0;

  const showDocumentList =
    fetching || (!fetching && Array.isArray(documents) && documents.length > 0);

  const updatePropertyDocsAndFoldersToRender = (folderId) => {
    setCurrentDocFolderId(folderId);
    let foldersToRender = propertyDocumentFolders;
    propertyFoldersBreadCrumbs.forEach((breadcrumb) => {
      foldersToRender = foldersToRender.find(
        (el) => el.id === breadcrumb.id
      ).directories;
    });
    setPropertyDocumentFoldersToRender(foldersToRender);
    setSelectedPropertyFolderId(folderId);
    setPropertyDocumentsToRender(
      documents && documents.filter((doc) => doc.directoryId === folderId)
    );
  };

  const onPropertyFolderChange = (id) => {
    updatePropertyDocsAndFoldersToRender(id);
    const currentFolder = propertyDocumentFoldersToRender.find(
      (folder) => folder.id === id
    );
    setPropertyFoldersBreadCrumbs([
      ...propertyFoldersBreadCrumbs,
      currentFolder,
    ]);
  };

  useEffect(() => {
    updatePropertyDocsAndFoldersToRender(
      propertyFoldersBreadCrumbs[propertyFoldersBreadCrumbs.length - 1]?.id ||
        null
    );
  }, [propertyDocumentFolders, propertyFoldersBreadCrumbs]);

  useEffect(() => {
    if (history.action !== 'POP') {
      setPropertyFoldersBreadCrumbs([]);
    }
  }, [history]);

  useEffect(() => {
    setSearchQuery(documentDebouncedSearch);
  }, [documentDebouncedSearch]);

  useEffect(() => {
    setSearchQuery('');
    setSearchText('');
  }, [propertyFoldersBreadCrumbs]);

  const renderDocumentTable = () =>
    createElement(isDesktop ? getDocumentTable() : getDocumentList(), {
      fetching,
      documents: propertyDocumentsToRender,
      onFolderChange: onPropertyFolderChange,
      documentFolders: propertyDocumentFoldersToRender,
      directoryId: directoryId || currentDocFolderId,
      setDirectoryId,
      directoryTree,
      setDirectoryTree,
      searchQuery,
      fetchingError,
      headerHeight,
      setStickyFiltersHeight,
      showRoomsColumn: true,
      showEventsColumn: true,
      showBulkSelectionOptions: true,
      isBoligmappaPlusUser: true,
      publicSection: true,
      actionsEnabled: false,
    });

  const mobileHeaderSearch = () => (
    <MobileDocumentSearch
      fullWidth
      value={searchText}
      onChange={onSearchChange}
      placeholder="Søk i dokumenter"
      isSearching={isSearching}
      setIsSearching={setIsSearching}
    />
  );

  const changeBreadCrumb = (index) =>
    setPropertyFoldersBreadCrumbs(
      propertyFoldersBreadCrumbs.slice(0, index + 1)
    );

  const rootBreadCrumb = () => setPropertyFoldersBreadCrumbs([]);

  const renderHeaderBreadCrumbs = () => {
    const breadCrumbsNames = propertyFoldersBreadCrumbs
      .map((el) => el.name)
      .slice(0, -1);
    return (
      <Box display="flex" alignItems="center">
        <>
          <div>
            <button
              type="button"
              className={classes.breadCrumbButton}
              onClick={rootBreadCrumb}
            >
              <p style={{ color: '#717171' }}>Alle dokumenter</p>
            </button>
          </div>
          {breadCrumbsNames.length > 0 && (
            <div style={{ color: '#717171' }}>/</div>
          )}
        </>
        {breadCrumbsNames.map((el, index) => (
          <>
            {index + 1 !== breadCrumbsNames.lenght && (
              <div>
                <button
                  type="button"
                  className={classes.breadCrumbButton}
                  onClick={() => changeBreadCrumb(index)}
                >
                  <p style={{ color: '#717171' }}>{el}</p>
                </button>
              </div>
            )}
            {index + 1 !== breadCrumbsNames.length && (
              <div style={{ color: '#717171' }}>/</div>
            )}
          </>
        ))}
      </Box>
    );
  };

  const renderMobileBreadCrumbs = () =>
    propertyFoldersBreadCrumbs.length > 0 ? (
      <BreadCrumbs
        breadCrumbs={propertyFoldersBreadCrumbs}
        breadCrumbsNames={propertyFoldersBreadCrumbs.map((el) => el.name)}
        setBreadCrumbs={setPropertyFoldersBreadCrumbs}
        onlyBack
        pathClassName={classes.breadcrumbs}
      />
    ) : (
      <Typography
        className={isDesktop ? classes.headerTitle : classes.headerTitleMobile}
      >
        Alle dokumenter
      </Typography>
    );

  return (
    <WithHeader
      topLevelLayout
      minFullHeight
      noPadding={!isDesktop}
      setHeaderHeight={setHeaderHeight}
      hideHeader={isDesktop}
      isSearching={isSearching}
      headerTitle={renderMobileBreadCrumbs()}
      headerSearch={mobileHeaderSearch()}
      publicSection
      lessMarginLeft={isDesktop}
      maxWidth="lg"
    >
      {fetchingError && (
        <ErrorContainer
          customErrorObject={{
            title: PUBLICSECTIONFORBIDDENERROR,
            message: '',
          }}
          paddingTop={isDesktop}
          errorResponse={fetchingError}
          style={{ paddingTop: isDesktop ? undefined : '15%' }}
        />
      )}
      {showDocumentList && (
        <div style={{ marginTop: stickyFiltersHeight }}>
          <Box px={!isDesktop ? 2 : 0} width={isDesktop ? '50%' : '100%'}>
            {isDesktop && (
              <SearchField
                value={searchText}
                placeholder="Søk i dokumenter"
                onChange={onSearchChange}
                className={clsx(isDesktop && classes.desktopSearchField)}
              />
            )}
          </Box>

          <div className={classes.breadCrumbsWrapper}>
            {propertyFoldersBreadCrumbs.length > 0 ? (
              <Box flex="none">
                {renderHeaderBreadCrumbs()}
                <Typography
                  className={
                    isDesktop ? classes.headerTitle : classes.headerTitleMobile
                  }
                >
                  {propertyFoldersBreadCrumbs.slice(-1)[0].name}
                </Typography>
              </Box>
            ) : (
              <Typography
                className={
                  isDesktop ? classes.headerTitle : classes.headerTitleMobile
                }
              >
                Alle dokumenter
              </Typography>
            )}
          </div>

          {isDesktop && <br />}

          <Box mt={2}>{renderDocumentTable()}</Box>
        </div>
      )}
      {showEmptyListMessage && (
        <EmptyMessage publicSection>Det finnes ingen dokumenter.</EmptyMessage>
      )}
    </WithHeader>
  );
};

Documents.defaultProps = {
  classes: {},
  documents: null,
};

Documents.propTypes = {
  classes: PropTypes.object, // eslint-disable-line
  setDocuments: PropTypes.func.isRequired,
  documents: PropTypes.arrayOf(
    PropTypes.shape({
      chapterTags: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          tagName: PropTypes.string,
        })
      ),
      companyId: PropTypes.number,
      companyName: PropTypes.string,
      createdDate: PropTypes.string,
      description: PropTypes.string,
      documentFileAvailable: PropTypes.bool,
      documentType: PropTypes.string,
      documentTypeId: PropTypes.number,
      events: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          link: PropTypes.string,
          title: PropTypes.string,
          image: PropTypes.string,
        })
      ),
      fileName: PropTypes.string,
      id: PropTypes.number,
      industry: PropTypes.string,
      jobDate: PropTypes.string,
      ownedById: PropTypes.number,
      ownedByName: PropTypes.string,
      rooms: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          link: PropTypes.string,
          title: PropTypes.string,
          image: PropTypes.string,
        })
      ),
      title: PropTypes.string,
      updatedById: PropTypes.number,
      updatedDate: PropTypes.string,
      url: PropTypes.shape({
        fileType: PropTypes.string,
        link: PropTypes.string,
        rel: PropTypes.string,
      }),
    })
  ),
};

const mapStateToProps = (state) => ({
  documents: sGetDocuments(state),
  searchText: sGetDocumentSearchText(state),
  propertyDocumentFolders: sGetPropertyDocumentFolders(state),
  propertyDocumentFoldersToRender: sGetPropertyDocumentFoldersToRender(state),
  propertyFoldersBreadCrumbs: sGetPropertyFoldersBreadCrumbs(state),
  fetching: sGetFetchingDocuments(state),
  fetchingError: sGetFetchingDocumentsError(state),
});

const mapDispatchToProps = (dispatch) => ({
  setDocuments: (documents) => dispatch(acSetDocuments(documents)),
  setSearchText: (searchText) => dispatch(acSetDocumentSearchText(searchText)),
  setPropertyDocumentFolders: (folders) =>
    dispatch(acSetPropertyDocumentFolders(folders)),
  setPropertyDocumentFoldersToRender: (folders) =>
    dispatch(acSetPropertyDocumentFoldersToRender(folders)),
  setPropertyFoldersBreadCrumbs: (breadCrumbs) =>
    dispatch(acSetPropertyFoldersBreadCrumbs(breadCrumbs)),
  setSelectedPropertyFolderId: (id) =>
    dispatch(acSetSelectedPropertyFolderId(id)),
  setFetching: (fetching) => dispatch(acSetFetchingDocuments(fetching)),
  setFetchingError: (fetchingError) =>
    dispatch(acSetFetchingDocumentsError(fetchingError)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(Documents));
